"use client";

import { useState } from "react";
import { normalizeDisplayName } from "@/lib/display-name";
import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import { useAccountData } from "./account-data-context";

export default function ProfileBasicsPanel() {
  const supabase = getSupabaseBrowserClient();
  const { name, email, isLoading, setName } = useAccountData();
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const nameInput = nameDraft ?? (name ?? "");
  const savedName = name?.trim() ? name.trim() : null;

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    setStatus(null);

    const nextName = normalizeDisplayName(nameInput);
    const { error } = await supabase.auth.updateUser({
      data: {
        name: nextName,
      },
    });

    if (error) {
      setStatus("Error");
      setIsSaving(false);
      return;
    }

    setName(nextName);
    setNameDraft(null);
    setStatus("Saved");
    setIsSaving(false);
    window.setTimeout(() => setStatus(null), 1600);
  };

  const isDirty = normalizeDisplayName(nameInput) !== savedName;

  return (
    <section className="panel p-6">
      <p className="font-[var(--font-display)] text-sm uppercase tracking-[0.22em] text-[var(--text)]">
        Profile
      </p>
      <div className="mt-4 space-y-3 text-sm text-[var(--muted)]">
        <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
          Name
          <input
            type="text"
            name="name"
            placeholder="Your name"
            autoComplete="name"
            value={nameInput}
            onChange={(event) => setNameDraft(event.target.value)}
            className="rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-[var(--text)] outline-none transition focus:border-[var(--accent)]"
          />
        </label>
        <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
          <span>Email</span>
          <span className="text-[var(--text)]">
            {isLoading ? "--" : email ?? "--"}
          </span>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <button
          type="button"
          onClick={handleSave}
          disabled={isLoading || isSaving || !isDirty}
          className="rounded-full border border-white/20 bg-white/5 px-5 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-[var(--text)] transition hover:border-white/40 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSaving ? "Saving..." : "Save"}
        </button>
        {status ? (
          <span className="text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
            {status}
          </span>
        ) : null}
      </div>
    </section>
  );
}
